/**
 * Google Analytics 4 via gtag.js (deferred load).
 *
 * Route changes are tracked from App.tsx so SPA navigations are counted.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID = "G-76DTR64YJJ";

let initPromise: Promise<void> | null = null;

function loadGtagScript(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = new Promise((resolve, reject) => {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", MEASUREMENT_ID, { send_page_view: false });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load gtag.js"));
    document.head.appendChild(script);
  });

  return initPromise;
}

export function ensureGoogleAnalytics(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();

  const run = () => loadGtagScript().catch(() => {});

  if ("requestIdleCallback" in window) {
    return new Promise((resolve) => {
      window.requestIdleCallback(() => {
        void run().then(resolve);
      });
    });
  }

  return new Promise((resolve) => {
    window.setTimeout(() => {
      void run().then(resolve);
    }, 1);
  });
}

export function trackPageView(pagePath: string): void {
  void ensureGoogleAnalytics().then(() => {
    if (!window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: pagePath,
      send_to: MEASUREMENT_ID,
    });
  });
}
